import type { WorldAgent, WorldBuilding, WorldFloor } from "@uzverse/contracts";
import { FLOOR_HEIGHT_M } from "@uzverse/world-scene";
import type { SceneNode } from "@uzverse/world-scene";
import { doorOf, buildingBox } from "../generators.js";
import type { DoorSpec } from "../generators.js";
import type { Bounds, Collider } from "../camera/walk.js";
import { themeOf } from "../brand.js";
import type { FloorTheme } from "../brand.js";
import { effectiveTheme, floorKind, furnitureLayout, roomFrame } from "./furniture.js";
import type { FloorKind, FurnitureLayout } from "./furniture.js";
import { seatPlan } from "./themes.js";
import { SHAFT, shaftWalls } from "../elevator/dims.js";
import type { CabinSlot } from "../elevator/dims.js";

/** The slab under a floor (matches the generator). */
export const SLAB_T = 0.3;
export const WALL_T = 0.3;

/** Y of the walkable surface of floor `level` in the building's local frame. */
export const floorSurfaceY = (level: number): number => level * FLOOR_HEIGHT_M + SLAB_T;

export interface RoomLayout {
  width: number;
  depth: number;
  /** Floor levels the elevator offers (the building's drawn floors, lowest first). */
  levels: number[];
  door: DoorSpec;
  /** Walkable area (the inside of the walls). */
  bounds: Bounds;
  /** The elevator shafts' walls (solid, doorways open), local frame, on every floor. */
  colliders: Collider[];
  /** The first cabin's block. */
  elevator: { x: number; z: number; size: number };
  /** The second cabin's block: a cabin in buildings of more than six floors, a service door else. */
  annex: { x: number; z: number; size: number };
  /** Where the cabins stand (one, or two in buildings of more than six floors). */
  cabins: CabinSlot[];
}

/** Floors above which a building gets a second cabin. */
export const TWO_CABINS_ABOVE = 6;

/** The shared room every floor uses: an empty hall with the elevator bank in the back corners. */
export function roomLayout(params: Record<string, unknown>): RoomLayout {
  const box = buildingBox(params, { windows: false });
  const door = doorOf(params);
  const hw = box.width / 2 - WALL_T;
  const hd = box.depth / 2 - WALL_T;
  // The back is opposite the door; the first cabin sits in the back-left corner, the second
  // (or the service door of a one-cabin building) in the back-right corner.
  const [nx, nz] = door.normal;
  const along = nx === 0 ? "x" : "z";
  const back = (nx === 0 ? -nz : -nx) as 1 | -1;
  const elevSize = SHAFT.size;
  const place = (lateral: number, depthFromBack: number): [number, number] =>
    along === "x" ? [lateral, back * (hd - depthFromBack)] : [back * (hw - depthFromBack), lateral];
  const lat = along === "x" ? hw : hd;
  const depth = elevSize / 2 + 0.2;
  const [ex, ez] = place(-lat + elevSize / 2 + 0.2, depth);
  const [sx, sz] = place(lat - elevSize / 2 - 0.2, depth);
  const yaw = yawIntoRoom(door);
  const cabins: CabinSlot[] = [{ x: ex, z: ez, yaw }];
  if (box.levels.length > TWO_CABINS_ABOVE) cabins.push({ x: sx, z: sz, yaw });
  const colliders: Collider[] = cabins.flatMap((c) => shaftWalls(c));
  return {
    width: box.width,
    depth: box.depth,
    levels: box.levels,
    door,
    bounds: { minX: -hw, maxX: hw, minZ: -hd, maxZ: hd },
    colliders,
    elevator: { x: ex, z: ez, size: elevSize },
    annex: { x: sx, z: sz, size: elevSize },
    cabins,
  };
}

/** The solid blocks of the room on one floor (their tops are absolute heights in the local frame). */
export function floorColliders(room: RoomLayout, level: number): Collider[] {
  const top = floorSurfaceY(level) + 3.4;
  return room.colliders.map((c) => ({ ...c, top }));
}

/** Yaw (radians, local frame) that faces from the door into the room. */
export function yawIntoRoom(door: DoorSpec): number {
  // forward = (-sin yaw, -cos yaw) must equal -normal.
  return Math.atan2(door.normal[0], door.normal[1]);
}

/** Yaw (radians, local frame) that faces out through the door. */
export function yawOutOfRoom(door: DoorSpec): number {
  return Math.atan2(-door.normal[0], -door.normal[1]);
}

export interface FloorRow {
  level: number;
  name: string | null;
  /** The floor's theme from the scene (or guessed from its name); null when it names none. */
  theme: FloorTheme | null;
  lead: WorldAgent | null;
  workers: WorldAgent[];
}

/** The floor list of the elevator: scene floors with names and agents from the org route when known. */
export function floorRows(
  layout: Pick<RoomLayout, "levels">,
  building: WorldBuilding | null,
): FloorRow[] {
  const byLevel = new Map<number, WorldFloor>();
  for (const f of building?.floors ?? []) byLevel.set(f.level, f);
  const levels = new Set<number>(layout.levels);
  for (const l of byLevel.keys()) if (l >= 0) levels.add(l);
  return [...levels]
    .sort((a, b) => a - b)
    .map((level) => {
      const f = byLevel.get(level);
      return {
        level,
        name: f?.name ?? null,
        theme: themeOf(f),
        lead: f?.lead ?? null,
        workers: f?.workers ?? [],
      };
    });
}

export interface Marker {
  agent: WorldAgent;
  role: "lead" | "worker";
  /** Local frame position on the floor surface. */
  x: number;
  z: number;
  /** Turn about Y that makes the person face their desk (the door side of the hall). */
  yaw: number;
}

/**
 * Where the floor's agents stand: the lead near the back of the hall, the workers on a grid of
 * desks in front of them. Deterministic, so tests and screenshots are stable.
 */
export function agentMarkers(
  room: RoomLayout,
  floor: FloorRow,
  options: { kind?: FloorKind } = {},
): Marker[] {
  const out: Marker[] = [];
  const theme = effectiveTheme(room, options.kind ?? "open", floor.theme);
  const frame = roomFrame(room);
  const plan = theme === "generic" ? null : seatPlan(theme, frame);
  if (plan) {
    if (floor.lead && plan.lead) {
      const [x, z] = frame.place(plan.lead.lat, plan.lead.dep);
      out.push({ agent: floor.lead, role: "lead", x, z, yaw: frame.yaw });
    }
    floor.workers.forEach((agent, i) => {
      const seat = plan.workers[i];
      if (!seat) return; // no seat left: the agent is not drawn
      const [x, z] = frame.place(seat.lat, seat.dep);
      out.push({ agent, role: "worker", x, z, yaw: frame.yaw });
    });
    return out;
  }
  const { bounds } = room;
  const [nx, nz] = room.door.normal;
  const toward = (lateral: number, depthFromBack: number): [number, number] => {
    if (nx === 0) {
      const back = -nz;
      return [lateral, back * (Math.abs(nz) * bounds.maxZ - depthFromBack)];
    }
    const back = -nx;
    return [back * (Math.abs(nx) * bounds.maxX - depthFromBack), lateral];
  };
  const span = nx === 0 ? bounds.maxX : bounds.maxZ;
  if (floor.lead) {
    const [x, z] = toward(0, 3.2);
    out.push({ agent: floor.lead, role: "lead", x, z, yaw: frame.yaw });
  }
  const perRow = Math.max(1, Math.min(4, Math.floor((span * 2 - 4) / 3)));
  floor.workers.forEach((agent, i) => {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const lateral = (col - (perRow - 1) / 2) * 3;
    // The same limits as the desks of `furnitureLayout`: no seat where there is no desk.
    if (row >= 3 || 7 + row * 3 > frame.length - 4) return;
    const [x, z] = toward(lateral, 7 + row * 3);
    out.push({ agent, role: "worker", x, z, yaw: frame.yaw });
  });
  return out;
}

/** May the user step into this building? Members (the org route lists it) and the public hub. */
export function canEnter(node: SceneNode, org: WorldBuilding | null, isLandmark: boolean): boolean {
  if (node.geometry.kind !== "procedural" || node.geometry.generator !== "building.box")
    return false;
  return org !== null || isLandmark;
}

export interface FloorPlan {
  floor: FloorRow | null;
  kind: FloorKind;
  theme: FloorTheme;
  markers: Marker[];
  layout: FurnitureLayout;
}

/** Everything that stands on one floor: its theme, the seated agents and the furniture. */
export function floorPlan(room: RoomLayout, rows: readonly FloorRow[], level: number): FloorPlan {
  const floor = rows.find((r) => r.level === level) ?? null;
  const kind = floorKind(
    level,
    rows.map((r) => r.level),
  );
  const markers = floor ? agentMarkers(room, floor, { kind }) : [];
  const layout = furnitureLayout(room, markers, {
    ground: level === 0,
    kind,
    theme: floor?.theme ?? null,
  });
  return { floor, kind, theme: layout.theme, markers, layout };
}
